import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';

import { type Token } from '@bv/api';
import { useToast } from '@bv/composables';
import { getLocaleCode, trans } from '@bv/utils/i18n';
import { goToConsole } from '@bv/utils/url/console/go-to';

import { withAuthRefreshLock } from './auth-refresh-lock';

// Extend Axios types to include custom properties
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    silent?: boolean;
    logout?: boolean;
    fullResponse?: boolean;
    _retry?: boolean;
    skipRefresh?: boolean;
    returnFullError?: boolean;
  }

  export interface AxiosRequestConfig {
    // allow passing `fullResponse` in the public request config as well
    // so that calls like `http.request({ ..., fullResponse: true })` are type-safe.
    fullResponse?: boolean;
  }
}

const SILENT_HEADER = 'x-silent';
const RETURN_FULL_ERROR = 'x-return-full-error';
const LOGOUT_HEADER = 'x-logout';
const SKIP_REFRESH_HEADER = 'x-skip-refresh';

/**
 * Parses the 'x-...' header and returns the appropriate value for the config.
 */
const getHeaderConfig = (headerValue: string | string[] | undefined): boolean | number[] | undefined => {
  if (headerValue === 'true') {
    return true;
  }

  if (Array.isArray(headerValue)) {
    // Convert array of strings to array of numbers
    return headerValue.map(Number).filter((n) => !isNaN(n));
  }

  return undefined;
};

/**
 * Handles API error responses, parsing messages and showing toasts.
 */
export const handleApiError = (error: AxiosError<any>): void => {
  const { response, config } = error;
  const status = response?.status;
  const data = response?.data;

  if (status === 403 && data?.error?.code === 'service_not_allowed') {
    goToConsole();
    return;
  }

  // Don't show toast if the request was marked as silent for this status code
  if (
    config?.silent === true ||
    (Array.isArray(config?.silent) && status && config?.silent.includes(status) && data.silent)
  ) {
    return;
  }

  const { error: toastError } = useToast();

  switch (status) {
    case 422: // Validation errors
      if (data?.errors) {
        // Flatten all error messages from the nested object into a single array
        const messages = Object.values(data.errors).flat();
        messages.forEach((msg: any) => toastError(String(msg)));
      } else {
        toastError(data?.message || trans('global.response_error.422_trans'));
      }
      break;

    case 500:
      if (import.meta.env.PROD) {
        toastError(trans('global.response_error.500_trans'));
      } else {
        toastError(data?.message || trans('global.response_error.500_trans'));
      }
      break;

    default:
      // For all other errors (403, 404, etc.)
      if (data?.message) {
        toastError(data.message);
      } else if (error.message) {
        toastError(error.message); // For network errors etc.
      }
      break;
  }
};

/**
 * Configured Axios HTTP client instance
 * @type {AxiosInstance}
 */
const httpInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL + '/' + import.meta.env.VITE_API_VERSION,
  headers: {
    Accept: '*',
    'Access-Control-Allow-Origin': '*',
  },
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Re-reads the shared auth cookies and, when another tab has already rotated the
 * refresh token, returns that tab's access token to adopt. Returns null when no
 * rotation happened (or cookies were cleared by a logout elsewhere).
 */
const adoptTokensRotatedByAnotherTab = (
  authStore: { syncTokensFromCookies: () => void; refreshToken?: string | null; token?: string | null },
  staleRefreshToken: string,
): string | null => {
  authStore.syncTokensFromCookies();

  if (authStore.refreshToken && authStore.refreshToken !== staleRefreshToken && authStore.token) {
    return authStore.token;
  }

  return null;
};

/**
 * Rotates the access token via the refresh endpoint, coordinated across tabs.
 * Returns the access token to use (either newly issued, or already rotated by another tab).
 */
const performTokenRefresh = async (staleRefreshToken: string): Promise<string> => {
  const { useAuthStore } = await import('@bv/stores/auth');
  const authStore = useAuthStore();

  return withAuthRefreshLock(async () => {
    // Cookies are shared across tabs; re-read after acquiring the lock in case another tab already rotated.
    const adopted = adoptTokensRotatedByAnotherTab(authStore, staleRefreshToken);
    if (adopted) {
      return adopted;
    }

    try {
      const refreshResponse = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/${import.meta.env.VITE_API_VERSION}/auth/refresh-token`,
        {},
        {
          headers: { 'refresh-token': authStore.refreshToken },
        },
      );

      const newToken: Token = refreshResponse.data.data.token;

      authStore.setToken(newToken.access_token, newToken.expires_in);
      authStore.setRefreshToken(newToken.refresh_token, newToken.refresh_expires_in);

      return newToken.access_token;
    } catch (refreshError) {
      // The POST may have lost a race to a tab outside our origin-scoped lock.
      const adopted = adoptTokensRotatedByAnotherTab(authStore, staleRefreshToken);
      if (adopted) {
        return adopted;
      }

      throw refreshError;
    }
  });
};

/**
 * Request interceptor handler
 */
export const requestHandler = async (config: InternalAxiosRequestConfig) => {
  const { useAuthStore } = await import('@bv/stores/auth');
  const authStore = useAuthStore();
  const token: string | null = authStore.impersonateToken || authStore.token || authStore.refreshToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers['Accept-Language'] = getLocaleCode();

  const { useUserStore } = await import('@bv/stores');
  const userStore = useUserStore();

  if (!config.headers['x-workspace-id']) {
    config.headers['x-workspace-id'] = userStore.currentWorkspace?.id;
  }

  if (!config.headers['x-service-id']) {
    config.headers['x-service-id'] = userStore.currentService?.id;
  }

  if (config.data instanceof FormData) {
    config.headers['Content-Type'] = 'multipart/form-data';
  }

  // Handle the silent header
  if (config.headers[SILENT_HEADER]) {
    config.silent = getHeaderConfig(config.headers[SILENT_HEADER]) as boolean | undefined;
    delete config.headers[SILENT_HEADER];
  }

  // Handle the silent header
  if (config.headers[RETURN_FULL_ERROR]) {
    config.returnFullError = getHeaderConfig(config.headers[RETURN_FULL_ERROR]) as boolean | undefined;
    delete config.headers[RETURN_FULL_ERROR];
  }

  // Handle the logout header
  if (config.headers[LOGOUT_HEADER]) {
    config.logout = getHeaderConfig(config.headers[LOGOUT_HEADER]) as boolean | undefined;
    delete config.headers[LOGOUT_HEADER];
  }

  // Handle the skip refresh header
  if (config.headers[SKIP_REFRESH_HEADER]) {
    config.skipRefresh = getHeaderConfig(config.headers[SKIP_REFRESH_HEADER]) as boolean | undefined;
    delete config.headers[SKIP_REFRESH_HEADER];
  }

  return config;
};

/**
 * Request interceptor
 */
httpInstance.interceptors.request.use(requestHandler, (error: AxiosError) => Promise.reject(error));

/**
 * Response success handler
 */
export const responseSuccessHandler = (response: AxiosResponse) => {
  if (response.config.fullResponse) {
    return response;
  }
  return response.data;
};

/**
 * Response error handler
 */
export const responseErrorHandler = async (error: AxiosError) => {
  // if the error is due to request cancellation using abortController, we don't want to show a toast or process it as an error
  if (axios.isCancel(error)) {
    return Promise.reject({ message: 'Request cancelled', isCancelled: true });
  }

  const { config, response } = error;
  const originalRequest = config as InternalAxiosRequestConfig;

  if (response?.status === 401 && !originalRequest._retry && !originalRequest.logout && !originalRequest.skipRefresh) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return httpInstance(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const { useAuthStore } = await import('@bv/stores/auth');
    const authStore = useAuthStore();
    const refreshToken = authStore.refreshToken;

    if (refreshToken) {
      try {
        const newAccessToken = await performTokenRefresh(refreshToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return httpInstance(originalRequest);
      } catch (refreshError) {
        const adopted = adoptTokensRotatedByAnotherTab(authStore, refreshToken);
        if (adopted) {
          originalRequest.headers.Authorization = `Bearer ${adopted}`;
          processQueue(null, adopted);
          return httpInstance(originalRequest);
        }

        processQueue(refreshError, null);

        // Log out only when the dead refresh token is still current, or when another tab
        // already cleared the cookies — otherwise this tab would be stranded with no redirect.
        if (!authStore.refreshToken || authStore.refreshToken === refreshToken) {
          useAuthStore().logout();
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      useAuthStore()
        .logout()
        .finally(() => {
          isRefreshing = false;
        });
    }
  }

  handleApiError(error);

  if (config?.returnFullError) {
    return Promise.reject(error);
  }

  // Attach HTTP status so Vue Query retry logic can skip non-transient 4xx errors.
  const payload = response?.data;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return Promise.reject({ ...payload, httpStatus: response?.status });
  }

  return Promise.reject(payload || error);
};

/**
 * Response interceptor
 */
httpInstance.interceptors.response.use(responseSuccessHandler, responseErrorHandler);

export default httpInstance;
